'use client';

import { useMemo, useState } from 'react';
import {
  computeFraction,
  formatNumber,
  parseFiniteNumber,
  toImproper,
  type FractionOp,
} from '@/lib/browser-mathcalc-tools';

const OPS: { label: string; value: FractionOp }[] = [
  { label: 'Add (+)', value: 'add' },
  { label: 'Subtract (−)', value: 'subtract' },
  { label: 'Multiply (×)', value: 'multiply' },
  { label: 'Divide (÷)', value: 'divide' },
];

type Side = 'a' | 'b';

export default function BrowserFractionCalculator() {
  const [aWhole, setAWhole] = useState('');
  const [aNum, setANum] = useState('1');
  const [aDen, setADen] = useState('2');
  const [bWhole, setBWhole] = useState('');
  const [bNum, setBNum] = useState('1');
  const [bDen, setBDen] = useState('3');
  const [op, setOp] = useState<FractionOp>('add');

  const result = useMemo(() => {
    const aDenNum = parseFiniteNumber(aDen);
    const bDenNum = parseFiniteNumber(bDen);
    const aNumNum = parseFiniteNumber(aNum);
    const bNumNum = parseFiniteNumber(bNum);
    if (aDenNum === null || bDenNum === null || aNumNum === null || bNumNum === null) {
      return { error: 'Enter a numerator and denominator for both fractions.' };
    }
    const a = toImproper(parseFiniteNumber(aWhole) ?? 0, aNumNum, aDenNum);
    const b = toImproper(parseFiniteNumber(bWhole) ?? 0, bNumNum, bDenNum);
    return computeFraction(a, b, op);
  }, [aWhole, aNum, aDen, bWhole, bNum, bDen, op]);

  const error = 'error' in result ? result.error : null;
  const data = 'error' in result ? null : result;

  function fractionInputs(side: Side) {
    const isA = side === 'a';
    return (
      <div className="tool-live-field tool-live-field-full">
        <span>{isA ? 'Fraction A' : 'Fraction B'} (whole, numerator, denominator)</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            aria-label={`${isA ? 'A' : 'B'} whole number`}
            inputMode="numeric"
            onChange={(event) => (isA ? setAWhole : setBWhole)(event.target.value)}
            placeholder="whole"
            style={{ flex: 1 }}
            type="text"
            value={isA ? aWhole : bWhole}
          />
          <input
            aria-label={`${isA ? 'A' : 'B'} numerator`}
            inputMode="numeric"
            onChange={(event) => (isA ? setANum : setBNum)(event.target.value)}
            placeholder="num"
            style={{ flex: 1 }}
            type="text"
            value={isA ? aNum : bNum}
          />
          <input
            aria-label={`${isA ? 'A' : 'B'} denominator`}
            inputMode="numeric"
            onChange={(event) => (isA ? setADen : setBDen)(event.target.value)}
            placeholder="den"
            style={{ flex: 1 }}
            type="text"
            value={isA ? aDen : bDen}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Fractions</span>
        <h2 className="tool-live-title">Fraction Calculator</h2>
        <p className="tool-live-subtitle">
          Add, subtract, multiply or divide two fractions (with optional whole parts) and see the
          reduced result, decimal and steps. Runs live in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {fractionInputs('a')}
            <label className="tool-live-field tool-live-field-full">
              <span>Operation</span>
              <select onChange={(event) => setOp(event.target.value as FractionOp)} value={op}>
                {OPS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {fractionInputs('b')}
          </div>
          <p className="tool-live-panel-note">
            Leave the whole box blank for a simple fraction. Use a negative whole number for a
            negative mixed number.
          </p>
        </section>

        <section className="tool-live-preview">
          {error ? (
            <p className="status-message" style={{ color: '#dc2626' }}>
              {error}
            </p>
          ) : data ? (
            <>
              <div className="tool-html-meta-grid">
                <div className="tool-live-field">
                  <span>Result (lowest terms)</span>
                  <input
                    readOnly
                    type="text"
                    value={`${data.reduced.numerator}/${data.reduced.denominator}`}
                  />
                </div>
                <div className="tool-live-field">
                  <span>Mixed number</span>
                  <input readOnly type="text" value={data.mixed} />
                </div>
                <div className="tool-live-field">
                  <span>Decimal</span>
                  <input readOnly type="text" value={formatNumber(data.decimal, 6)} />
                </div>
              </div>
              <label
                className="tool-live-field tool-live-field-full"
                style={{ marginTop: '0.75rem' }}
              >
                <span>Steps</span>
                <textarea readOnly rows={data.steps.length + 1} value={data.steps.join('\n')} />
              </label>
              <p className="tool-live-panel-note">Calculated locally in your browser.</p>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}
